import { useState, useEffect, useMemo, useRef } from "react";
import { BASE_H, SUPABASE_URL, SUPABASE_KEY, authH } from "../config";
import {
  AGREEMENT_FIELDS, SIGNATURE_KEY, agreementValues, blankFields, cleanBoxes, customerKey, fieldLabel, fillTemplate, hasSignaturePlaceholder,
  isCustomersUnit, isEditableField, isOtherField, needsBusinessSignature, otherFields, otherKey, otherName, templateFieldList, unknownFields, vatSetting, withOverrides,
} from "../shared/agreementFields";
import { PdfPages } from "./PdfPages";
import { FieldBoxesOverlay, boxColours } from "./FieldBoxes";
import { AgreementText } from "./AgreementText";
import { NewCustomerModal } from "./NewCustomerModal";
import { useConfirm } from "./ConfirmDialog";
import { BusinessSignModal } from "./BusinessSignModal";
import { BusinessSettings } from "./BusinessSettings";

// ─── Agreements & e-signature ─────────────────────────────────────────────────
// Templates (text with fill-in fields, or the business's own PDF with boxes marked on it) and agreements
// sent for signing, filled in with the customer's details and the unit(s) they're renting.
// Sending and signing go through /api/esign (api/esign.js). Template PDFs live in the private "agreements"
// storage bucket, apart from the Documents page; the signed PDF is saved in the unit's Documents.

const NEW_CUSTOMER = "__new";
const sb = fetch.bind(window);
async function ok(r, what) {
  if (r.ok) return r;
  let msg = ""; try { const d = await r.json(); msg = d.message || d.error || ""; } catch {}
  throw new Error(`${what} failed${msg ? `: ${msg}` : ` (${r.status})`}`);
}

// ── Template PDFs: the private "agreements" bucket ──
async function uploadTemplatePdf(file, token) {
  const path = `templates/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
  await ok(await sb(`${SUPABASE_URL}/storage/v1/object/agreements/${path}`, {
    method: "POST", headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${token}`, "Content-Type": "application/pdf" }, body: file,
  }), "Uploading the PDF");
  return path;
}
async function templatePdfUrl(path, token) {
  const r = await sb(`${SUPABASE_URL}/storage/v1/object/sign/agreements/${path}`, { method: "POST", headers: authH(token), body: JSON.stringify({ expiresIn: 3600 }) });
  const d = await r.json().catch(() => ({}));
  return d.signedURL ? `${SUPABASE_URL}/storage/v1${d.signedURL}` : null;
}

// ── The e-signature API ──
async function esignCall(action, body, token) {
  const r = await sb(`/api/esign?action=${action}`, {
    method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify(body),
  });
  try { return await r.json(); } catch { return { error: `Error ${r.status}` }; }
}
const sendForSignature = (params, token) => esignCall("send", params, token);
const withdrawSignature = (requestId, token) => esignCall("cancel", { requestId }, token);
// Sign for the business, after the customer: { requestId, name, title, typedName, signatureImage, consent }
const countersignAgreement = (params, token) => esignCall("countersign", params, token);
// The filled-in agreement as a PDF, for checking before sending. Nothing is sent or saved.
async function previewAgreement(params, token) {
  const r = await sb("/api/esign?action=send", {
    method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ ...params, preview: true }),
  });
  if (r.ok && r.headers.get("Content-Type") === "application/pdf") return { pdf: await r.arrayBuffer() };
  try { return { error: (await r.json()).error || `Error ${r.status}` }; } catch { return { error: `Error ${r.status}` }; }
}

const REQUEST_COLUMNS = "id,tenant_id,unit_ids,title,kind,status,signer_name,signer_email,sent_at,viewed_at,signed_at,signed_pdf_path,expires_at,cancelled_at,needs_business_signature,business_signer_name,business_signed_at";
const STATUS = {
  customer_signed: ["Sign for business", "#FFF1E6", "#9A4B00"],
  sent: ["Sent", "#EEF4FB", "var(--navy)"],
  viewed: ["Opened", "#FFF8E6", "#8A6100"],
  signed: ["Signed", "#EBF5F0", "var(--success)"],
  cancelled: ["Withdrawn", "#F1F3F5", "var(--sub)"],
};
const fmt = d => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const todayIso = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });
const SAMPLE_UNITS = [
  { id: "A12", label: "A12", tenant: "Jo Bloggs", address: "1 High Street, Cambridge CB1 1AA", email: "jo@example.com", phone: "07700 900123", size: "20ft", row_name: "Row A", rent: 95, vat_rent: 114, tenant_deposit: 50, lock_deposit_amount: 15, move_in_date: todayIso() },
  { id: "B3", label: "B3", tenant: "Jo Bloggs", size: "10ft", row_name: "Row B", rent: 40, tenant_deposit: 20 },
];
const GROUPS = [...new Set(AGREEMENT_FIELDS.map(f => f.group))];

// In CamStorage a customer's details are on each unit they rent; units with the same email (or name) are one customer
// (customerKey, from shared/agreementFields.js).
const byUnitId = (a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true });
// After a field is dragged into the text: a space either side of inline fields, and the units table and
// signature on lines of their own.
const tidyDroppedFields = body => body
  .replace(/([^\n])[ \t]*(\{\{(?:units_table|signature)\}\})/g, "$1\n$2")
  .replace(/(\{\{(?:units_table|signature)\}\})[ \t]*([^\n])/g, "$1\n$2")
  .replace(/([^\s(£\n])(\{\{[a-z_]+(?::[^{}\n]+)?\}\})/g, "$1 $2")
  .replace(/(\{\{[a-z_]+(?::[^{}\n]+)?\}\})([^\s.,;:)\n])/g, "$1 $2");
const listOf = items => (items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`);
const unitList = r => (r.unit_ids?.length ? r.unit_ids : [r.tenant_id]).join(" + ");
const money = v => (v === null || v === undefined || v === "" ? "—" : `£${Number(v).toFixed(2)}`);

const BLANK_SEND = { customer: "", unitIds: [], startDate: "", templateId: "", signerName: "", signerEmail: "", message: "", overrides: {} };
const notice = (tone = "warn") => ({ marginTop: 10, padding: "8px 12px", borderRadius: 9, fontSize: 13,
  ...(tone === "warn" ? { background: "#FFF8E6", border: "1.5px solid #F5E0A0" } : { background: "#F1F6FB", border: "1.5px solid #D5E3F0" }) });

export function AgreementsPage({ token, data = [], showToast, onSaveUnit, onAddUnit }) {
  // CamStorage has no staff roles: everyone who can log in can manage templates and send.
  const canManage = true, canSend = true;
  const [settings, setSettings] = useState(null);   // app_settings 'agreements': { business_name, vat }
  const org = useMemo(() => ({ name: settings?.business_name || "CamStorage", settings: { vat: settings?.vat } }), [settings]);
  const [confirmBox, askConfirm] = useConfirm();
  const [templates, setTemplates] = useState(null);
  const [requests, setRequests] = useState(null);
  const [send, setSend] = useState(BLANK_SEND);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [preview, setPreview] = useState(null);   // { pdf } | { loading } while checking before sending
  const [editor, setEditor] = useState(null);     // { id?, kind, name, body | file/pdfPath/source/fields/placing }
  const [saving, setSaving] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [otherDraft, setOtherDraft] = useState("");   // name being typed for a new Other field
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [missingCopies, setMissingCopies] = useState({});   // request id → true when its signed copy is gone
  const [businessSigning, setBusinessSigning] = useState(null);   // the request being signed for the business
  const bodyRef = useRef(null);

  async function load() {
    const [t, r, s] = await Promise.all([
      sb(`${SUPABASE_URL}/rest/v1/agreement_templates?archived=eq.false&order=created_at.desc`, { headers: authH(token) }),
      sb(`${SUPABASE_URL}/rest/v1/signature_requests?select=${REQUEST_COLUMNS}&order=sent_at.desc&limit=200`, { headers: authH(token) }),
      sb(`${SUPABASE_URL}/rest/v1/app_settings?key=eq.agreements&select=value`, { headers: authH(token) }),
    ]);
    setTemplates(t.ok ? await t.json() : []);
    setRequests(r.ok ? await r.json() : []);
    setSettings(s.ok ? (await s.json())[0]?.value || {} : {});
  }
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Choosing the customer and units ──
  const customers = useMemo(() => {
    const map = new Map();
    for (const u of data.filter(u => u.tenant && !u.archived)) {
      const k = customerKey(u);
      if (!map.has(k)) map.set(k, { key: k, name: u.tenant, email: u.email || "", units: [] });
      map.get(k).units.push(u);
    }
    return [...map.values()].map(c => ({ ...c, units: c.units.sort(byUnitId) })).sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);
  const customer = customers.find(c => c.key === send.customer);
  const customerUnit = customer?.units[0];   // the customer's details come from their own units
  const chosenUnits = useMemo(() => send.unitIds.map(id => data.find(u => u.id === id)).filter(Boolean), [send.unitIds, data]);
  // Units they're moving into: chosen, but not theirs yet (vacant, or someone else is still in them).
  const movingInto = chosenUnits.filter(u => customerUnit && !isCustomersUnit(u, customerUnit));
  const otherUnits = useMemo(() => (customer ? data.filter(u => !u.archived && !u.deleted_at && !customer.units.some(c => c.id === u.id)).sort(byUnitId) : []), [data, customer]);
  const autoValues = useMemo(() => agreementValues({ units: chosenUnits, org, startDate: send.startDate || undefined, customer: customerUnit }), [chosenUnits, org, send.startDate, customerUnit]);
  const values = useMemo(() => withOverrides(autoValues, send.overrides), [autoValues, send.overrides]);   // with anything typed over them
  const vat = vatSetting(org);

  function pickCustomer(key, units) {
    if (key === NEW_CUSTOMER) { setAddingCustomer(true); return; }
    const list = units || customers.find(x => x.key === key)?.units || [];
    const moveIns = list.map(u => u.move_in_date).filter(Boolean).sort();
    setSend(s => ({ ...s, customer: key, unitIds: list.map(u => u.id), startDate: moveIns[0] || todayIso(), signerName: list[0]?.tenant || "", signerEmail: list[0]?.email || "" }));
    setSendResult(null); setPreview(null);
  }

  // A customer just added in the pop-up: choose them, with the units they were given.
  function customerAdded(units) {
    setAddingCustomer(false);
    pickCustomer(customerKey(units[0]), units);
    showToast(`✅ ${units[0].tenant} added to ${units.map(u => u.label || u.id).join(", ")}`);
  }
  const toggleUnit = id => { setSend(s => ({ ...s, unitIds: s.unitIds.includes(id) ? s.unitIds.filter(x => x !== id) : [...s.unitIds, id] })); setPreview(null); };

  const chosenTemplate = templates?.find(t => t.id === send.templateId);
  const usedKeys = chosenTemplate ? (chosenTemplate.kind === "text" ? chosenTemplate.body : (chosenTemplate.fields || []).map(f => f.key)) : [];
  const missing = chosenTemplate && chosenUnits.length ? blankFields(usedKeys, values) : [];
  const businessSigns = chosenTemplate && needsBusinessSignature(chosenTemplate.kind === "text" ? chosenTemplate.body : (chosenTemplate.fields || []).map(f => f.key));
  const noSignatureSpot = chosenTemplate && (chosenTemplate.kind === "text" ? !hasSignaturePlaceholder(chosenTemplate.body) : !(chosenTemplate.fields || []).some(f => f.key === SIGNATURE_KEY));
  const pdfWithoutFields = chosenTemplate?.kind === "pdf" && !(chosenTemplate.fields || []).some(f => f.key !== SIGNATURE_KEY && f.key !== "date_signed");
  const fieldsToFill = templateFieldList(chosenTemplate);   // every field the agreement fills in, in page order
  const sendParams = { templateId: send.templateId, unitIds: send.unitIds, customerUnitId: customerUnit?.id, startDate: send.startDate, signerName: send.signerName, signerEmail: send.signerEmail, message: send.message,
    overrides: Object.fromEntries(fieldsToFill.filter(f => isEditableField(f.key) && send.overrides[f.key] !== undefined).map(f => [f.key, send.overrides[f.key]])) };
  const setOverride = (key, text) => { setSend(s => { const o = { ...s.overrides }; if (text === undefined) delete o[key]; else o[key] = text; return { ...s, overrides: o }; }); setPreview(null); };

  async function showPreview() {
    if (!send.templateId || !send.unitIds.length) { showToast("❌ Choose the customer, their units and the agreement first"); return; }
    setPreview({ loading: true });
    const d = await previewAgreement(sendParams, token);
    if (d.error) { setPreview(null); showToast(`❌ ${d.error}`); return; }
    setPreview(d);
  }

  async function handleSend(e) {
    e.preventDefault();
    if (!send.unitIds.length) { showToast("❌ Tick at least one unit"); return; }
    if (missing.length && !(await askConfirm({
      title: "Some details are empty",
      message: `These are empty for ${send.signerName || "this customer"}, so they'll show as blank lines in the agreement:\n\n• ${missing.join("\n• ")}\n\nYou can fill them in first (Go back), or send it as it is.`,
      ok: "Send anyway", cancel: "Go back",
    }))) return;
    setSending(true); setSendResult(null);
    const d = await sendForSignature(sendParams, token);
    setSending(false);
    if (d.error) { showToast(`❌ ${d.error}`); return; }
    setSendResult(d);
    if (d.emailSent) showToast(`✅ Sent to ${send.signerEmail} for signing`);
    setSend(s => ({ ...BLANK_SEND, templateId: s.templateId }));
    setPreview(null);
    load();
  }

  async function withdraw(r) {
    if (!(await askConfirm({ title: "Withdraw this agreement?", message: `"${r.title}" sent to ${r.signer_name} — their link will stop working.`, ok: "Withdraw", danger: true }))) return;
    const d = await withdrawSignature(r.id, token);
    if (d.error) { showToast(`❌ ${d.error}`); return; }
    showToast("✅ Withdrawn");
    load();
  }

  // The signed copy is kept in the unit's Documents, so it goes with them when the customer is archived or deleted.
  async function openSigned(r) {
    const res = await sb(`${SUPABASE_URL}/storage/v1/object/sign/documents/${r.signed_pdf_path}`, {
      method: "POST", headers: { ...BASE_H, Authorization: `Bearer ${token}` }, body: JSON.stringify({ expiresIn: 3600 }),
    }).catch(() => null);
    const d = res ? await res.json().catch(() => ({})) : {};
    if (d.signedURL) { window.open(`${SUPABASE_URL}/storage/v1${d.signedURL}`, "_blank", "noopener"); return; }
    if (res && (res.status === 404 || /not.?found/i.test(`${d.error} ${d.message} ${d.statusCode}`))) {
      setMissingCopies(m => ({ ...m, [r.id]: true }));
      showToast(`❌ The signed copy of "${r.title}" has been removed from ${unitList(r)}'s Documents — the customer was probably archived or deleted. If they were archived, it's with their details in Archive.`);
      return;
    }
    showToast("❌ Couldn't open the signed copy just now — please try again");
  }

  // The document exactly as the customer was sent it. The tab is opened straight away (browsers block one opened later).
  async function openSent(r) {
    const tab = window.open("", "_blank");
    const res = await sb(`/api/esign?action=sent&id=${encodeURIComponent(r.id)}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null);
    if (res?.ok && res.headers.get("Content-Type") === "application/pdf") {
      const url = URL.createObjectURL(await res.blob());
      if (tab) tab.location.href = url; else window.location.assign(url);
      return;
    }
    tab?.close();
    let msg = ""; try { msg = (await res.json()).error || ""; } catch {}
    showToast(`❌ Couldn't open what was sent${msg ? `: ${msg}` : " — please try again"}`);
  }

  // ── Templates ──
  async function editTemplate(t) {
    if (t.kind === "text") { setEditor({ id: t.id, kind: "text", name: t.name, body: t.body || "" }); return; }
    const url = await templatePdfUrl(t.pdf_path, token);
    if (!url) { showToast("❌ Couldn't open that PDF"); return; }
    setPageCount(0);
    setEditor({ id: t.id, kind: "pdf", name: t.name, pdfPath: t.pdf_path, pdfName: t.pdf_original_name, source: url, fields: t.fields || [], placing: "customer_name" });
  }

  async function chooseFile(file) {
    if (!file) return;
    if (file.type !== "application/pdf") { showToast("❌ That file isn't a PDF"); return; }
    setPageCount(0);
    const source = await file.arrayBuffer();
    setEditor(ed => ({ ...ed, file, source, fields: [], name: ed.name || file.name.replace(/\.pdf$/i, "") }));
  }

  async function saveTemplate(e) {
    e.preventDefault();
    if (!editor.name.trim()) { showToast("❌ Give the template a name"); return; }
    setSaving(true);
    try {
      const row = { name: editor.name.trim(), kind: editor.kind };
      if (editor.kind === "text") {
        if (!editor.body.trim()) throw new Error("Write or paste the agreement text");
        row.body = editor.body;
      } else {
        if (!editor.file && !editor.pdfPath) throw new Error("Choose your PDF");
        if (editor.file) {
          row.pdf_path = await uploadTemplatePdf(editor.file, token);
          row.pdf_original_name = editor.file.name;
        }
        row.fields = cleanBoxes(editor.fields, pageCount || Infinity);
      }
      const url = editor.id ? `${SUPABASE_URL}/rest/v1/agreement_templates?id=eq.${editor.id}` : `${SUPABASE_URL}/rest/v1/agreement_templates`;
      await ok(await sb(url, {
        method: editor.id ? "PATCH" : "POST", headers: { ...authH(token), Prefer: "return=minimal" },
        body: JSON.stringify(editor.id ? { ...row, updated_at: new Date().toISOString() } : row),
      }), "Saving template");
      showToast(`✅ Template "${row.name}" saved`);
      setEditor(null);
      load();
    } catch (err) { showToast(`❌ ${err.message}`); }
    setSaving(false);
  }

  async function archiveTemplate(t) {
    if (!(await askConfirm({ title: "Remove this template?", message: `"${t.name}" — agreements already sent are not affected.`, ok: "Remove", danger: true }))) return;
    try {
      await ok(await sb(`${SUPABASE_URL}/rest/v1/agreement_templates?id=eq.${t.id}`, {
        method: "PATCH", headers: { ...authH(token), Prefer: "return=minimal" }, body: JSON.stringify({ archived: true }),
      }), "Removing template");
      showToast("✅ Template removed"); load();
    } catch (err) { showToast(`❌ ${err.message}`); }
  }

  // Inserts {{field}} where the cursor is; tables and the signature go on a line of their own.
  function insertField(f) {
    const ta = bodyRef.current;
    const tag = `{{${f.key}}}`;
    setEditor(ed => {
      const at = ta ? ta.selectionStart : ed.body.length, end = ta ? ta.selectionEnd : ed.body.length;
      const before = ed.body.slice(0, at), after = ed.body.slice(end);
      const ownLine = f.block || f.key === SIGNATURE_KEY;
      // inline fields get a space either side when they'd otherwise run into the words around them
      const piece = ownLine
        ? `${before && !before.endsWith("\n") ? "\n" : ""}${tag}${after.startsWith("\n") ? "" : "\n"}`
        : `${/[^\s(£]$/.test(before) ? " " : ""}${tag}${/^[^\s.,;:)]/.test(after) ? " " : ""}`;
      requestAnimationFrame(() => { if (ta) { ta.focus(); ta.selectionStart = ta.selectionEnd = at + piece.length; } });
      return { ...ed, body: before + piece + after };
    });
  }
  const typos = editor?.kind === "text" ? unknownFields(editor.body) : [];
  // Each field in the text with a few words either side, so a misplaced one is easy to spot.
  const placedFields = editor?.kind === "text"
    ? [...editor.body.matchAll(/\{\{\s*([a-z_]+|other\s*:[^{}\n]+?)\s*\}\}/g)].map(m => ({
        label: /^other\s*:/.test(m[1]) ? `Other: ${otherName(m[1].replace(/^other\s*:/, ""))}` : AGREEMENT_FIELDS.find(f => f.key === m[1])?.label || m[1],
        before: editor.body.slice(Math.max(0, m.index - 35), m.index).replace(/\s+/g, " "),
        after: editor.body.slice(m.index + m[0].length, m.index + m[0].length + 35).replace(/\s+/g, " "),
      }))
    : [];
  // Other fields already in the template being edited (so they can be added again or dragged).
  const editorOthers = editor ? otherFields(editor.kind === "text" ? editor.body : editor.fields.map(f => f.key)) : [];
  const sampleValues = useMemo(() => agreementValues({ units: SAMPLE_UNITS, org, other: Object.fromEntries(editorOthers.map(n => [n, `[${n} — typed in when sending]`])) }), [org, editorOthers.join("\n")]); // eslint-disable-line react-hooks/exhaustive-deps

  const fieldList = (onPick, active, kinds, draggable) => (
    <div style={{ display: "grid", gap: 6, marginBottom: 8 }}>
      {GROUPS.map(g => (
        <div key={g} style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          <span className="tsm" style={{ width: 110, color: "var(--sub)", fontWeight: 600 }}>{g}</span>
          {AGREEMENT_FIELDS.filter(f => f.group === g && kinds(f)).map(f => (
            <button type="button" key={f.key} onClick={() => onPick(f)} title={draggable ? `Drag into your text, or click to add it where your cursor is: {{${f.key}}}` : `{{${f.key}}}`}
              draggable={draggable} onDragStart={draggable ? e => { e.dataTransfer.setData("text/plain", `{{${f.key}}}`); e.dataTransfer.effectAllowed = "copy"; } : undefined}
              style={{ cursor: "pointer", borderRadius: 6, fontSize: 12, padding: "3px 8px", textAlign: "left", ...(active === f.key ? { ...boxColours(f.key), fontWeight: 700 } : { background: f.signing ? "#FFF8E6" : "var(--mist)", border: "1px solid #D8E2EE", color: "var(--text)" }) }}>
              {f.label} <code style={{ fontSize: 10, color: "var(--sub)" }}>{`{{${f.key}}}`}</code>
            </button>
          ))}
        </div>
      ))}
      {/* Other: anything not in the list above — named here, typed in when sending */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
        <span className="tsm" style={{ width: 110, color: "var(--sub)", fontWeight: 600 }}>Other</span>
        {editorOthers.map(n => {
          const f = { key: otherKey(n), label: n };
          return (
            <button type="button" key={f.key} onClick={() => onPick(f)} title={draggable ? `Drag into your text, or click to add it where your cursor is` : `Mark where "${n}" goes`}
              draggable={draggable} onDragStart={draggable ? e => { e.dataTransfer.setData("text/plain", `{{${f.key}}}`); e.dataTransfer.effectAllowed = "copy"; } : undefined}
              style={{ cursor: "pointer", borderRadius: 6, fontSize: 12, padding: "3px 8px", ...(active === f.key ? { ...boxColours(f.key), fontWeight: 700 } : { background: "var(--mist)", border: "1px solid #D8E2EE", color: "var(--text)" }) }}>
              {n}
            </button>
          );
        })}
        <input value={otherDraft} onChange={e => setOtherDraft(e.target.value)} maxLength={60} placeholder="Name it, e.g. Gate code"
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addOther(onPick); } }} style={{ width: 190, fontSize: 12, padding: "4px 8px" }} />
        <button type="button" className="btn btn-outline btn-sm" onClick={() => addOther(onPick)} disabled={!otherName(otherDraft)}>+ Add Other field</button>
      </div>
      <div className="tsub tsm" style={{ marginLeft: 116, marginTop: -2 }}>For anything not listed — you type what goes in it each time you send.</div>
    </div>
  );
  function addOther(onPick) {
    let name = otherName(otherDraft);
    if (!name) return;
    // A name that's already used gets a number, so each new Other field has its own answer
    // (to put the same answer in two places, click the existing field instead).
    const taken = new Set(editorOthers.map(n => n.toLowerCase()));
    if (taken.has(name.toLowerCase())) { let n = 2; while (taken.has(`${name} (${n})`.toLowerCase())) n++; name = `${name} (${n})`; }
    onPick({ key: otherKey(name), label: name });
    setOtherDraft("");
  }
  // Renames one box's Other field (e.g. to split two "Yes or No" boxes into two questions).
  function renameBox(box, newName) {
    const name = otherName(newName);
    setEditor(ed => ({ ...ed, fields: ed.fields.map(b => (b === box ? { ...b, key: otherKey(name || "Other") } : b)) }));
  }

  const unplaced = editor?.kind === "pdf" ? { label: fieldLabel(editor.placing) } : null;
  const boxCount = editor?.kind === "pdf" ? editor.fields.length : 0;

  return (
    <div>
      {confirmBox}
      {businessSigning && <BusinessSignModal request={businessSigning} businessName={org?.name} onClose={() => setBusinessSigning(null)}
        onSign={async form => {
          const d = await countersignAgreement({ requestId: businessSigning.id, ...form }, token);
          if (d.error) return d;
          showToast(`✅ Signed for ${org?.name || "the business"} — the signed copy is saved in the unit's Documents and emailed to ${businessSigning.signer_name}`);
          setBusinessSigning(null); load(); return d;
        }} />}
      {addingCustomer && <NewCustomerModal data={data} org={org} onSaveUnit={onSaveUnit} onAddUnit={onAddUnit} onClose={() => setAddingCustomer(false)} onAdded={customerAdded} />}
      {/* Send for signature */}
      <div className="card">
        <div className="ch"><div className="ct">✍️ Send an agreement for signature</div></div>
        <div className="cb">
          {templates?.length === 0 ? (
            <p className="tsub tsm">{canManage ? "First add your agreement below — upload your own PDF and mark where each detail goes." : "An owner or admin needs to add an agreement template first."}</p>
          ) : (
            <form onSubmit={handleSend}>
              <div className="fg">
                <div className="fgi"><label>Customer</label>
                  <select value={send.customer} required onChange={e => pickCustomer(e.target.value)}>
                    <option value="">— Choose —</option>
                    {customers.map(c => <option key={c.key} value={c.key}>{c.name} — {c.units.map(u => u.label || u.id).join(", ")}</option>)}
                    {onSaveUnit && canSend && <option value={NEW_CUSTOMER}>+ New customer…</option>}
                  </select>
                  {onSaveUnit && canSend && <button type="button" className="tsm" onClick={() => setAddingCustomer(true)} style={{ background: "none", border: "none", color: "var(--navy)", textDecoration: "underline", cursor: "pointer", padding: "4px 0 0", textAlign: "left" }}>Not in the list? Add a new customer</button>}
                </div>
                <div className="fgi"><label>Agreement</label>
                  <select value={send.templateId} required onChange={e => { setSend(s => ({ ...s, templateId: e.target.value })); setPreview(null); }}>
                    <option value="">— Choose —</option>
                    {(templates || []).map(t => <option key={t.id} value={t.id}>{t.name}{t.kind === "pdf" ? " (PDF)" : ""}</option>)}
                  </select></div>
              </div>

              {customer && (
                <div style={{ marginTop: 10 }}>
                  <label className="tsm" style={{ fontWeight: 600 }}>Units they're renting</label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
                    {customer.units.map(u => (
                      <label key={u.id} style={{ display: "flex", gap: 6, alignItems: "center", padding: "6px 10px", border: "1.5px solid #D8E2EE", borderRadius: 8, cursor: "pointer", fontSize: 13, background: send.unitIds.includes(u.id) ? "#EEF4FB" : "#fff" }}>
                        <input type="checkbox" checked={send.unitIds.includes(u.id)} onChange={() => toggleUnit(u.id)} />
                        <strong>{u.label || u.id}</strong>{u.size ? ` · ${u.size}` : ""} · {money(u.rent)} ex VAT
                      </label>
                    ))}
                    {movingInto.map(u => (
                      <label key={u.id} style={{ display: "flex", gap: 6, alignItems: "center", padding: "6px 10px", border: "1.5px solid #B9D3EE", borderRadius: 8, cursor: "pointer", fontSize: 13, background: "#EEF4FB" }}>
                        <input type="checkbox" checked onChange={() => toggleUnit(u.id)} />
                        <strong>{u.label || u.id}</strong>{u.size ? ` · ${u.size}` : ""} · {money(u.rent)} ex VAT · <em>moving in</em>
                      </label>
                    ))}
                  </div>
                  {canSend && otherUnits.length > 0 && (
                    <select value="" onChange={e => { if (e.target.value) toggleUnit(e.target.value); }} style={{ marginTop: 8, maxWidth: 420 }} aria-label="Add a unit they're moving into">
                      <option value="">+ Add a unit they're moving into…</option>
                      {otherUnits.filter(u => !send.unitIds.includes(u.id)).map(u => (
                        <option key={u.id} value={u.id}>{u.label || u.id}{u.category ? ` · ${u.category}` : ""}{u.size ? ` · ${u.size}` : ""} · {u.tenant ? `${u.tenant} is in it now` : "vacant"}</option>
                      ))}
                    </select>
                  )}
                  {movingInto.some(u => u.tenant) && (
                    <div style={notice("info")}>
                      {listOf(movingInto.filter(u => u.tenant).map(u => `${u.label || u.id} (${u.tenant} is in it now)`))}: only the unit's own details and rent go in this agreement, not the current occupant's. Change the rent below if it will be different. The signed copy is saved in {customer.name}'s Documents, not with the current occupant.
                    </div>
                  )}
                </div>
              )}

              <div className="fg" style={{ marginTop: 10 }}>
                <div className="fgi"><label>Start / move-in date</label>
                  <input type="date" value={send.startDate} onChange={e => { setSend(s => ({ ...s, startDate: e.target.value })); setPreview(null); }} /></div>
                <div className="fgi"><label>Signer's name</label>
                  <input value={send.signerName} required onChange={e => setSend(s => ({ ...s, signerName: e.target.value }))} /></div>
                <div className="fgi"><label>Signer's email</label>
                  <input type="email" value={send.signerEmail} required onChange={e => setSend(s => ({ ...s, signerEmail: e.target.value }))} /></div>
              </div>

              {chosenUnits.length > 0 && (
                <div style={notice("info")}>
                  <strong>Filled in automatically:</strong> {values.customer_name || "—"} · {values.customer_address || <em>no address</em>} · {values.customer_email || <em>no email</em>} · {values.customer_phone || <em>no phone</em>}<br />
                  {values.unit_count} unit{values.unit_count === "1" ? "" : "s"}: {values.units_list} · from {values.start_date || "—"}<br />
                  Monthly: <strong>{values.total_ex_vat || "—"}</strong> ex VAT · VAT {values.total_vat || "—"} · <strong>{values.total_inc_vat || "—"}</strong> inc VAT
                  {!vat && <div className="tsm" style={{ marginTop: 4, color: "#8A6100" }}>Your VAT setting isn't set, so each unit's own ex/inc VAT rents are used as entered. Set it under “Business & VAT” at the bottom of this page to work out VAT automatically.</div>}
                </div>
              )}
              {chosenTemplate && fieldsToFill.length > 0 && (
                <div style={{ marginTop: 10, border: "1.5px solid #E4EAF2", borderRadius: 9, padding: "10px 12px", background: "#fff" }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>What goes in each box ({fieldsToFill.length}) — check them, and change any before sending</div>
                  <div style={{ display: "grid", gap: 6 }}>
                    {fieldsToFill.map(f => {
                      const signing = !isEditableField(f.key);
                      const changed = send.overrides[f.key] !== undefined && send.overrides[f.key] !== (autoValues[f.key] || "");
                      const shown = send.overrides[f.key] ?? autoValues[f.key] ?? "";
                      return (
                        <div key={f.key} style={{ display: "grid", gridTemplateColumns: "minmax(120px, 34%) 1fr", gap: 8, alignItems: "center", fontSize: 13 }}>
                          <div>
                            <strong>{f.label}</strong>{f.places > 1 && <span className="tsub tsm"> · in {f.places} places</span>}
                            {isOtherField(f.key) && <div className="tsub tsm">Other field — you type it</div>}
                          </div>
                          {signing
                            ? <div className="tsub tsm">{f.key === SIGNATURE_KEY ? "The customer signs here" : f.key === "date_signed" ? "Filled in when they sign" : f.key === "units_table" ? (values.units_list || "Your units and prices") : `Signed for ${org?.name || "the business"} after the customer`}</div>
                            : <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                <input value={shown} maxLength={2000} placeholder={isOtherField(f.key) ? "Type what goes here" : "Empty — type it here"} style={{ flex: 1, ...(shown ? {} : { borderColor: "#E8B931" }) }}
                                  onChange={e => setOverride(f.key, e.target.value)} />
                                {changed && !isOtherField(f.key) && <button type="button" className="tsm" title="Use the details from the unit again" onClick={() => setOverride(f.key, undefined)} style={{ background: "none", border: "none", color: "var(--navy)", textDecoration: "underline", cursor: "pointer", whiteSpace: "nowrap" }}>Undo</button>}
                              </div>}
                        </div>
                      );
                    })}
                  </div>
                  <div className="tsub tsm" style={{ marginTop: 6 }}>Changes here are only for this agreement — the unit's details aren't changed.</div>
                </div>
              )}
              <div className="fgi" style={{ marginTop: 10 }}><label>Message (optional)</label>
                <textarea rows={2} value={send.message} onChange={e => setSend(s => ({ ...s, message: e.target.value }))} placeholder="e.g. Here's your storage licence — please sign before your move-in date." /></div>
              {missing.length > 0 && <div style={notice()}>⚠️ Empty for this customer, so they'll show as blanks: <strong>{missing.join(", ")}</strong>. Fill them in first{missing.includes("Your VAT number") ? " (your VAT number is under “Business & VAT” below; the rest on the unit)" : " on the unit"}, or send anyway.</div>}
              {businessSigns && <div style={notice("info")}>✍️ This agreement is signed for {org?.name || "the business"} too: after {send.signerName || "the customer"} signs, it shows here as <strong>Sign for business</strong> — the final signed copy is made and emailed once someone from your business has signed.</div>}
              {noSignatureSpot && <div style={notice()}>⚠️ This agreement doesn't say where to sign, so the signature goes on an extra page at the end. {canManage && <>Edit the template to {chosenTemplate.kind === "text" ? <>add <code>{"{{signature}}"}</code></> : "mark a signature box"}.</>}</div>}
              {pdfWithoutFields && <div style={notice()}>⚠️ No boxes are marked on this PDF, so it's sent exactly as uploaded — nothing is filled in. {canManage && "Edit the template to mark where each detail goes."}</div>}
              <div className="fr" style={{ marginTop: 12 }}>
                <button className="btn btn-primary" disabled={sending}>{sending ? "Sending…" : "Send for signature"}</button>
                <button type="button" className="btn btn-outline" onClick={showPreview} disabled={preview?.loading}>{preview?.loading ? "Preparing…" : "👁 Preview what they'll see"}</button>
              </div>
              <div className="tsub tsm" style={{ marginTop: 8 }}>They get a private link by email, read the agreement, sign by typing (or drawing) their name, and you both receive the signed PDF with a signing certificate. It's also saved in each unit's Documents.</div>
              {sendResult && !sendResult.emailSent && (
                <div style={{ ...notice(), padding: "10px 14px" }}>
                  ⚠️ The email couldn't be sent ({sendResult.emailError}). Send them this private link yourself:<br /><code style={{ wordBreak: "break-all" }}>{sendResult.link}</code>
                </div>
              )}
              {preview?.pdf && (
                <div style={{ marginTop: 14 }}>
                  <div className="fr" style={{ justifyContent: "space-between", marginBottom: 6 }}>
                    <strong>Preview — the yellow box is where they'll sign</strong>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => setPreview(null)}>Close preview</button>
                  </div>
                  <PdfPages source={preview.pdf} maxHeight="75vh" />
                </div>
              )}
            </form>
          )}
        </div>
      </div>

      {/* Sent agreements */}
      <div className="card">
        <div className="ch"><div className="ct">Agreements sent</div>{requests && <span className="chip">{requests.length}</span>}
          <a href="/verify" target="_blank" rel="noopener noreferrer" className="tsm" style={{ marginLeft: "auto" }}>Check a signed document hasn't been changed</a></div>
        <div className="cb">
          {requests === null && <p className="tsub tsm">Loading…</p>}
          {requests?.length === 0 && <p className="tsub tsm">Nothing sent yet.</p>}
          {requests?.length > 0 && (
            <div className="tw"><table>
              <thead><tr><th>Unit</th><th>Agreement</th><th>Signer</th><th>Status</th><th>Sent</th><th>Signed</th><th></th></tr></thead>
              <tbody>{requests.map(r => {
                const [label, bg, color] = STATUS[r.status] || [r.status, "#eee", "#333"];
                const expired = !["signed", "cancelled", "customer_signed"].includes(r.status) && new Date(r.expires_at) < new Date();
                return (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{unitList(r)}</td>
                    <td>{r.title}</td>
                    <td>{r.signer_name}<div className="tsub tsm">{r.signer_email}</div></td>
                    <td><span className="chip" style={{ background: bg, color }}>{expired ? "Expired" : label}</span></td>
                    <td className="tsm">{fmt(r.sent_at)}</td>
                    <td className="tsm">{fmt(r.signed_at)}</td>
                    <td>
                      <button className="btn btn-outline btn-sm" onClick={() => openSent(r)} title="The agreement exactly as it was sent to the customer, before signing">👁 What was sent</button>{" "}
                      {r.status === "signed" && (missingCopies[r.id]
                        ? <span className="tsm tsub" title="It was kept in the unit's Documents, which were archived or deleted with the customer">Signed copy removed</span>
                        : <button className="btn btn-outline btn-sm" onClick={() => openSigned(r)}>📄 Signed copy</button>)}
                      {r.status === "customer_signed" && <button className="btn btn-primary btn-sm" onClick={() => setBusinessSigning(r)}>✍️ Sign for the business</button>}
                      {r.status === "signed" && r.business_signer_name && <div className="tsub tsm">Signed for the business by {r.business_signer_name}</div>}
                      {["sent", "viewed", "customer_signed"].includes(r.status) && <button className="btn btn-danger btn-sm" onClick={() => withdraw(r)}>Withdraw</button>}
                    </td>
                  </tr>
                );
              })}</tbody>
            </table></div>
          )}
        </div>
      </div>

      {/* Templates */}
      <div className="card">
        <div className="ch">
          <div className="ct">Your agreement templates</div>
          {canManage && !editor && (
            <div className="fr">
              {/* New templates are uploaded PDFs only (owner's choice, to keep it simple); older wording templates still work and can be edited. */}
              <button className="btn btn-primary btn-sm" onClick={() => { setPageCount(0); setEditor({ kind: "pdf", name: "", file: null, fields: [], placing: "customer_name" }); }}>+ Upload your PDF</button>
            </div>
          )}
        </div>
        <div className="cb">
          {editor && (
            <form onSubmit={saveTemplate} style={{ background: "#F8FAFC", border: "1.5px dashed #C9D8E8", borderRadius: 9, padding: 16, marginBottom: 16 }}>
              <div className="fgi"><label>Template name</label>
                <input value={editor.name} onChange={e => setEditor(ed => ({ ...ed, name: e.target.value }))} placeholder="e.g. Self-Storage Licence Agreement" /></div>
              {editor.kind === "text" ? (
                <>
                  <div className="tsub tsm" style={{ margin: "10px 0 6px" }}>
                    Paste your agreement, then put each field where that detail goes — <strong>drag a field into the text</strong>, or click in the text first and then click the field.
                    Replace blanks like "______" with the field. When you send, each field is filled in with that customer's details and units.
                    Put <code>{"{{signature}}"}</code> where they should sign (their name and the date go with it).
                  </div>
                  {fieldList(insertField, null, () => true, true)}
                  <textarea ref={bodyRef} rows={16} value={editor.body} onChange={e => { const body = e.nativeEvent?.inputType === "insertFromDrop" ? tidyDroppedFields(e.target.value) : e.target.value; setEditor(ed => ({ ...ed, body })); }} style={{ width: "100%", fontFamily: "var(--fb)", fontSize: 13 }}
                    placeholder={"SELF-STORAGE LICENCE AGREEMENT\n\nThis licence is between {{business_name}} and {{customer_name}} of {{customer_address}}.\n\nStart date: {{start_date}}\nUnits ({{unit_count}}):\n{{units_table}}\nMonthly total: {{total_ex_vat}} + VAT {{total_vat}} = {{total_inc_vat}}\n\n1. ...\n\nSigned by the customer:\n{{signature}}"} />
                  {placedFields.length > 0 && (
                    <details style={{ marginTop: 6 }}><summary className="tsm" style={{ cursor: "pointer" }}>Where your fields are ({placedFields.length}) — check each is in the right place</summary>
                      <div style={{ background: "#fff", border: "1px solid #E4EAF2", borderRadius: 8, padding: "6px 10px", marginTop: 6, fontSize: 12, maxHeight: 220, overflow: "auto" }}>
                        {placedFields.map((p, i) => (
                          <div key={i} style={{ padding: "3px 0", borderBottom: "1px solid #F1F4F8" }}>
                            <span className="tsub">…{p.before}</span><strong style={{ background: "#EEF4FB", borderRadius: 4, padding: "0 4px" }}>{p.label}</strong><span className="tsub">{p.after}…</span>
                          </div>
                        ))}
                      </div></details>
                  )}
                  {typos.length > 0 && <div style={{ color: "var(--danger)", fontSize: 12 }}>⚠️ Not recognised (check spelling): {typos.map(t => `{{${t}}}`).join(", ")}</div>}
                  {editor.body.trim() && !hasSignaturePlaceholder(editor.body) && <div style={{ color: "#8A6100", fontSize: 12, marginTop: 4 }}>Tip: add <code>{"{{signature}}"}</code> where they sign — otherwise the signature goes on an extra page at the end.</div>}
                  {editor.body.trim() && (
                    <details style={{ marginTop: 8 }}><summary className="tsm" style={{ cursor: "pointer" }}>Preview with example details (2 units)</summary>
                      <div style={{ background: "#fff", border: "1px solid #E4EAF2", borderRadius: 8, padding: 14, marginTop: 6, maxHeight: 420, overflow: "auto" }}>
                        <AgreementText text={fillTemplate(editor.body, sampleValues)} style={{ fontSize: 13 }} />
                      </div></details>
                  )}
                </>
              ) : (
                <>
                  {!editor.pdfPath && (
                    <div className="fgi" style={{ marginTop: 10 }}><label>Your agreement (PDF)</label>
                      <input type="file" accept="application/pdf" onChange={e => chooseFile(e.target.files?.[0])} /></div>
                  )}
                  {editor.pdfPath && <div className="tsm" style={{ marginTop: 8 }}>PDF: <strong>{editor.pdfName}</strong></div>}
                  {editor.source && (
                    <>
                      <div className="tsub tsm" style={{ margin: "10px 0 6px" }}>
                        <strong>Mark where each detail goes.</strong> Pick a field, then drag a box on the page where it should be written (or just click for a standard size).
                        Drag a box to move it, its corner to resize, × to remove. Mark a <strong>Signature</strong> box where the customer signs — their name and the signing date are written along the bottom of it.
                      </div>
                      {fieldList(f => setEditor(ed => ({ ...ed, placing: f.key })), editor.placing, () => true)}
                      <div className="tsm" style={{ margin: "4px 0 8px" }}>
                        Placing: <strong>{unplaced?.label || "—"}</strong> · {boxCount} box{boxCount === 1 ? "" : "es"} marked
                        {!editor.fields.some(f => f.key === SIGNATURE_KEY) && <span style={{ color: "#8A6100" }}> · no signature box yet — the signature would go on an extra page at the end</span>}
                      </div>
                      {editor.fields.length > 0 && (
                        <details style={{ margin: "0 0 8px" }}><summary className="tsm" style={{ cursor: "pointer" }}>Boxes marked ({editor.fields.length}) — every box, even ones hidden behind another</summary>
                          <div style={{ background: "#fff", border: "1px solid #E4EAF2", borderRadius: 8, padding: "6px 10px", marginTop: 6, fontSize: 12, maxHeight: 260, overflow: "auto" }}>
                            {[...editor.fields].sort((a, b) => a.page - b.page || a.y - b.y || a.x - b.x).map((b, i) => (
                              <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", padding: "3px 0", borderBottom: "1px solid #F1F4F8" }}>
                                <span className="tsub" style={{ width: 50 }}>Page {b.page + 1}</span>
                                {isOtherField(b.key)
                                  ? <input defaultValue={fieldLabel(b.key)} title="Rename this Other field" style={{ flex: 1, fontSize: 12, padding: "2px 6px" }} onBlur={e => renameBox(b, e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }} />
                                  : <strong style={{ flex: 1 }}>{fieldLabel(b.key)}</strong>}
                                <button type="button" onClick={() => setEditor(ed => ({ ...ed, fields: ed.fields.filter(x => x !== b) }))} style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", fontSize: 12 }}>Remove</button>
                              </div>
                            ))}
                          </div></details>
                      )}
                      <PdfPages source={editor.source} onLoaded={setPageCount} maxHeight="80vh"
                        overlay={page => <FieldBoxesOverlay page={page} boxes={editor.fields} placing={editor.placing} onChange={fields => setEditor(ed => ({ ...ed, fields }))} />} />
                    </>
                  )}
                </>
              )}
              <div className="fr" style={{ marginTop: 12 }}>
                <button className="btn btn-primary" disabled={saving}>{saving ? "Saving…" : "Save template"}</button>
                <button type="button" className="btn btn-outline" onClick={() => setEditor(null)}>Cancel</button>
              </div>
              {editor.id && <div className="tsub tsm" style={{ marginTop: 6 }}>Changes apply to agreements you send from now on. Ones already sent stay exactly as they were.</div>}
            </form>
          )}
          {templates?.length === 0 && !editor && <p className="tsub tsm">No templates yet.</p>}
          {templates?.map(t => {
            const boxes = t.fields || [];
            const signs = t.kind === "pdf" ? boxes.some(b => b.key === SIGNATURE_KEY) : hasSignaturePlaceholder(t.body);
            return (
              <div key={t.id} className="user-card">
                <div className="user-info-block">
                  <div className="uemail">{t.name} <span className="chip">{t.kind === "pdf" ? "Your PDF" : "Template"}</span></div>
                  <div className="umeta">
                    {t.kind === "pdf" ? `${t.pdf_original_name || "PDF"} · ${boxes.length} box${boxes.length === 1 ? "" : "es"} marked` : `${(t.body || "").length.toLocaleString()} characters`}
                    {" · "}{signs ? "signature in place" : "signature on an extra page"}
                    {needsBusinessSignature(t.kind === "pdf" ? boxes.map(b => b.key) : t.body) && " · signed for the business too"} · added {fmt(t.created_at)}
                  </div>
                </div>
                {canManage && !editor && (
                  <div className="fr">
                    <button className="btn btn-outline btn-sm" onClick={() => editTemplate(t)}>{t.kind === "pdf" ? "Mark fields" : "Edit"}</button>
                    <button className="btn btn-danger btn-sm" onClick={() => archiveTemplate(t)}>Remove</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {settings && <BusinessSettings token={token} settings={settings} showToast={showToast} onSaved={setSettings} />}
    </div>
  );
}
